import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { midioRegistro } from '../../lib/pixel';
import { proveedoresActivados } from '../../lib/proveedores';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const modeFromUrl = searchParams.get('mode');
  
  const [isLogin, setIsLogin] = useState(modeFromUrl === 'login' || modeFromUrl !== 'register');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const { signIn, signUp, signInWithGoogle, resetPassword, user } = useAuth();
  const navigate = useNavigate();

  // Qué botones de "entrar con..." se pueden mostrar de verdad. Se le
  // pregunta al servidor en vez de darlo por hecho: el de Google estuvo
  // meses a la vista sin estar activado, y quien lo tocaba se quedaba en
  // una pantalla de error en inglés, sin forma de volver.
  const [social, setSocial] = useState({ google: false, facebook: false, apple: false });

  useEffect(() => {
    proveedoresActivados().then(setSocial);
  }, []);

  // Agregar Schema.org JSON-LD y meta tags dinámicos
  useEffect(() => {
    // Actualizar título de la página
    const pageTitle = isLogin 
      ? 'Entrar · Agenda Padres Bebés'
      : 'Crear cuenta · Agenda Padres Bebés';
    document.title = pageTitle;

    // Agregar meta description dinámica
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', isLogin 
        ? 'Inicia sesión en SOS Padres para acceder a tu cuenta y continuar monitoreando el cuidado de tu bebé.'
        : 'Crea tu cuenta gratuita en SOS Padres y comienza a registrar la alimentación, sueño, crecimiento y desarrollo de tu bebé.'
      );
    }

    // Agregar Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": isLogin ? "Iniciar Sesión - SOS Padres" : "Crear Cuenta - SOS Padres",
      "description": isLogin 
        ? "Página de inicio de sesión para acceder a tu cuenta de SOS Padres"
        : "Página de registro para crear una nueva cuenta en SOS Padres",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/auth`,
      "mainEntity": {
        "@type": isLogin ? "LoginAction" : "RegisterAction",
        "target": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/auth`,
        "object": {
          "@type": "WebApplication",
          "name": "SOS Padres"
        }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": isLogin ? "Iniciar Sesión" : "Crear Cuenta",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/auth`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      // Restaurar título original
      document.title = 'Agenda Padres Bebés · la app de SOS Padres para el día a día del bebé';
    };
  }, [isLogin]);

  // Solo redirigir después de un login/registro exitoso
  useEffect(() => {
    if (user && !loading && !error) {
      // Pequeña pausa para mostrar éxito antes de redirigir
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, loading, error, navigate]);

  // Establecer modo inicial basado en URL
  useEffect(() => {
    if (modeFromUrl === 'register') {
      setIsLogin(false);
    } else if (modeFromUrl === 'login') {
      setIsLogin(true);
    }
  }, [modeFromUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (isLogin) {
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          throw error;
        }
        setMessage('¡Bienvenido de vuelta!');
      } else {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Las contraseñas no coinciden');
        }
        if (formData.password.length < 6) {
          throw new Error('La contraseña debe tener al menos 6 caracteres');
        }
        const { error } = await signUp(formData.email, formData.password, formData.name);
        if (error) {
          throw error;
        }
        // La conversión principal del anuncio. Se mide acá y no al
        // confirmar el correo porque es el momento en que la persona
        // hizo lo que el anuncio le pedía.
        midioRegistro(new URLSearchParams(window.location.search).get('utm_source') ?? undefined);
        setMessage('¡Cuenta creada exitosamente! Revisa tu email para confirmar tu cuenta.');
      }
    } catch (error: any) {
      setError(error.message || 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    setSocialLoading(provider);
    setError('');
    setMessage('');

    try {
      const result = await signInWithGoogle();

      if (result.error) {
        throw result.error;
      }

      // El usuario será redirigido automáticamente por OAuth
    } catch (error: any) {
      setError(error.message || 'Error al conectar con Google');
    } finally {
      setSocialLoading(null);
    }
  };

  // Se eliminó `handleGoogleLogin`: era una copia de `handleSocialLogin`
  // que nadie llamaba y que además usaba `supabase` sin importarlo. Si
  // alguien la hubiera conectado a un botón, reventaba al tocarlo. El
  // botón de Google usa `handleSocialLogin('google')`, que sí funciona.

  const handleResetPassword = async () => {
    if (!formData.email) {
      setError('Ingresa tu email para restablecer la contraseña');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { error } = await resetPassword(formData.email);
      if (error) throw error;
      setMessage('Revisa tu email para restablecer tu contraseña');
    } catch (error: any) {
      setError(error.message || 'Error al enviar email');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setMessage('');
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    // Actualizar URL
    navigate(`/auth?mode=${!isLogin ? 'login' : 'register'}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Volver al inicio"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          
          <div className="w-20 h-20 mx-auto mb-4 relative">
            <img 
              src="/img/logo-sos.jpg"
              alt="Logo de SOS Padres"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-800" style={{fontFamily: '"Pacifico", serif'}}>
            Agenda Padres Bebés
          </h1>
          <p className="text-sm text-gray-500 mb-2">de SOS Padres</p>
          <p className="text-gray-600">
            {isLogin ? 'Inicia sesión en tu cuenta' : 'Crea tu nueva cuenta gratuita'}
          </p>
        </div>

        {/* Botones de "entrar con...". Solo aparecen si esa forma de
            entrar está activada en el panel de Supabase. */}
        {social.google && (
        <div className="mb-6">
          <button
            onClick={() => handleSocialLogin('google')}
            disabled={socialLoading !== null || loading}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {socialLoading === 'google' ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar con Google
              </>
            )}
          </button>
        </div>
        )}

        {/* La rayita de "o continúa con email" solo tiene sentido si
            arriba hay otra opción. Sola, confunde. */}
        {social.google && (
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">o continúa con email</span>
          </div>
        </div>
        )}

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            onClick={() => !isLogin && toggleMode()}
            className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
              isLogin 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Iniciar Sesión
          </button>
          <button
            onClick={() => isLogin && toggleMode()}
            className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
              !isLogin 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Crear Cuenta
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre completo
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tu nombre completo"
                aria-describedby="name-help"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="tu@email.com"
              aria-describedby="email-help"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Mínimo 6 caracteres"
              aria-describedby="password-help"
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar contraseña
              </label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Repite tu contraseña"
                aria-describedby="confirm-password-help"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm" role="alert">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-green-600 text-sm" role="alert">
              <i className="ri-check-line mr-2"></i>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || socialLoading !== null}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Procesando...
              </div>
            ) : (
              <>
                <i className={`${isLogin ? 'ri-login-box-line' : 'ri-user-add-line'} mr-2`}></i>
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center space-y-3">
          {isLogin && (
            <button
              onClick={handleResetPassword}
              disabled={loading || socialLoading !== null}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50"
            >
              <i className="ri-lock-password-line mr-1"></i>
              ¿Olvidaste tu contraseña?
            </button>
          )}
          
          {/* Antes esto era texto suelto: decía que aceptabas una política
              que no existía y no se podía leer por ningún lado. Ahora
              enlaza al documento real, que además explica que acá se
              guardan datos de salud de un bebé. */}
          <p className="text-xs text-gray-500">
            Al {isLogin ? 'iniciar sesión' : 'crear una cuenta'}, aceptas nuestra{' '}
            <a
              href="https://sospadres.info/privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 underline hover:text-blue-700"
            >
              política de privacidad
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
